import type { PaymentMethodViewDto, StripeSessionDto } from '@subscriptions/application/dtos';
import { buildCardExpiration, mapToPaymentMethodViewDto } from '@subscriptions/application/mappers';
import type { PaymentSessionMode } from '@subscriptions/domain/types/SubscriptionsConst';
import type {
  ConfirmSessionApiResponse,
  GenerateSessionApiResponse,
  PaymentMethodApiResponse,
} from '@subscriptions/infrastructure/api/types';

/** Mapper type for payment method API transformations */
export type PaymentMethodApiMapperType = {
  mapGenerateSessionResponseToDto: (response: GenerateSessionApiResponse) => StripeSessionDto;
  mapConfirmSessionResponseToViewDto: (response: ConfirmSessionApiResponse) => PaymentMethodViewDto;
  mapPaymentMethodApiResponseToViewDto: (response: PaymentMethodApiResponse) => PaymentMethodViewDto;
};

/**
 * Single translation of the canonical `providerPublic*` API keys into the
 * normalized shape consumed by the shared {@link mapToPaymentMethodViewDto}:
 * `brand` ← `providerPublicBrand`, `expirationDate` ← `providerPublicMonth`/
 * `providerPublicYear`, `lastFourDigits` ← `providerPublicId`. Every endpoint
 * that returns a payment method (confirm-session, generate-reference,
 * attach-payment-method, the `v1/payment-methods` listing) shares this shape,
 * so this is the only place the key translation lives — the per-endpoint
 * mapper methods delegate here.
 */
export const mapPaymentMethodApiResponseToViewDto = (response: PaymentMethodApiResponse): PaymentMethodViewDto =>
  mapToPaymentMethodViewDto({
    uuid: response.uuid,
    orgUuid: response.orgUuid,
    provider: response.provider,
    providerId: response.providerId,
    providerPublicId: response.providerPublicId,
    methodType: response.methodType,
    status: response.status,
    brand: response.providerPublicBrand,
    expirationDate: buildCardExpiration(response.providerPublicMonth, response.providerPublicYear),
    lastFourDigits: response.providerPublicId,
    createdAt: response.createdAt,
    updatedAt: response.updatedAt,
  });

/**
 * Creates mapper for payment method API type conversions. Both API shapes
 * (confirm-session and the canonical payment-method resource) expose the
 * canonical `providerPublic*` keys, so their methods delegate to the shared
 * {@link mapPaymentMethodApiResponseToViewDto} — the confirm-session entry is
 * kept as a named method so the repository stays readable per endpoint and a
 * future contract divergence has an obvious change point.
 */
export const createPaymentMethodApiMapper = (): PaymentMethodApiMapperType => {
  const mapGenerateSessionResponseToDto = (response: GenerateSessionApiResponse): StripeSessionDto => ({
    mode: response.mode as PaymentSessionMode,
    clientSecret: response.clientSecret,
    providerSessionId: response.providerSessionId,
  });

  // `ConfirmSessionApiResponse` is structurally the canonical payment-method shape,
  // so confirm-session delegates to the shared translation.
  const mapConfirmSessionResponseToViewDto = (response: ConfirmSessionApiResponse): PaymentMethodViewDto =>
    mapPaymentMethodApiResponseToViewDto(response);

  return { mapGenerateSessionResponseToDto, mapConfirmSessionResponseToViewDto, mapPaymentMethodApiResponseToViewDto };
};
